import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaClock,
  FaCalendarAlt,
  FaVideo,
  FaUserMd,
  FaPhoneAlt,
} from "react-icons/fa";
import ContactForm from "../components/forms/ContactForm";
import styles from "./Appointment.module.css";
import checkUpImage from ".././assets/appointment.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const Appointment = () => {
  const features = [
    {
      icon: <FaClock />,
      title: "Fast Confirmation",
      description: "Receive SMS/email confirmation within 24 hours",
    },
    {
      icon: <FaVideo />,
      title: "Teleconsultation",
      description: "WhatsApp or phone call at your scheduled time",
    },
    {
      icon: <FaUserMd />,
      title: "Expert Care",
      description: "Consult with Dr. Jack Kimeu and his team",
    },
    {
      icon: <FaCalendarAlt />,
      title: "Flexible Scheduling",
      description: "Choose in‑person or online appointments",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Book Appointment | Muigai Medical Center</title>
        <meta
          name="description"
          content="Schedule your appointment with Dr. Jack Kimeu – in‑person or teleconsultation. Quick confirmation and expert care."
        />
      </Helmet>

      {/* Hero Banner */}
      <section
        style={{ backgroundImage: `url(${checkUpImage})` }}
        className={styles.heroBanner}
      >
        <div className={styles.heroOverlay}></div>
        <div className="container">
          <motion.div
            className={styles.heroContent}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <motion.div className={styles.sectionTag} variants={fadeInUp}>
              BOOK NOW
            </motion.div>
            <motion.h1 variants={fadeInUp}>Schedule Your Appointment</motion.h1>
            <motion.p className={styles.heroSubtitle} variants={fadeInUp}>
              Choose in‑person or teleconsultation – we'll confirm within 24
              hours.
            </motion.p>
          </motion.div>
        </div>
        <div className={styles.heroBackground}>
          <div
            className={styles.floatingCircle}
            style={{ top: "15%", left: "10%", width: "120px", height: "120px" }}
          />
          <div
            className={styles.floatingCircle}
            style={{
              bottom: "20%",
              right: "15%",
              width: "80px",
              height: "80px",
            }}
          />
          <div
            className={styles.floatingCircle}
            style={{ top: "60%", left: "20%", width: "60px", height: "60px" }}
          />
        </div>
      </section>

      {/* Features Section */}
      <motion.section
        className={styles.featuresSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <div className="container">
          <div className={styles.featuresGrid}>
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                className={styles.featureCard}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: idx * 0.1 },
                  },
                }}
                whileHover={{ y: -8 }}
              >
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Form & Info Section */}
      <section className={styles.formSection}>
        <div className="container">
          <div className={styles.formGrid}>
            <div className={styles.formWrapper}>
              <ContactForm />
            </div>
            <div className={styles.infoWrapper}>
              <div className={styles.infoCard}>
                <h3>What to expect?</h3>
                <ul>
                  <li>
                    <FaCheckCircle className={styles.checkIcon} />
                    <span>Confirmation via SMS/Email within 24 hours</span>
                  </li>
                  <li>
                    <FaCheckCircle className={styles.checkIcon} />
                    <span>
                      For teleconsultation, you'll receive a WhatsApp or call at
                      the scheduled time
                    </span>
                  </li>
                  <li>
                    <FaCheckCircle className={styles.checkIcon} />
                    <span>
                      Please arrive 10 minutes early for in‑person visits
                    </span>
                  </li>
                  <li>
                    <FaCheckCircle className={styles.checkIcon} />
                    <span>
                      Bring any relevant medical records or test results
                    </span>
                  </li>
                </ul>
                <h3>Need urgent help?</h3>
                <p>
                  Call us directly:{" "}
                  <a href="tel:+254722265791">+254 722 265791</a>
                </p>
                <div className={styles.emergencyNote}>
                  <FaPhoneAlt />
                  <span>Emergency? Call immediately.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className="container">
          <div className={styles.ctaContent}>
            <h2>Ready to take charge of your health?</h2>
            <p>Book your appointment now – we're here to help.</p>
            <a href="tel:+254722265791" className={styles.ctaButton}>
              <FaPhoneAlt /> Call Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Appointment;
